import {
  CellClassParams,
  ColSpanParams,
  Column,
  GridApi,
  ICellRendererParams,
  IRowNode,
} from "@ag-grid-community/core";
import { FundFamilyUseAllocationTableAmountMeta } from "gen/model/fundFamilyUseAllocationTableAmountMeta";
import { FundFamilyUseAllocationTableAmountTypeDef } from "gen/model/fundFamilyUseAllocationTableAmountTypeDef";
import { FundFamilyUseAllocationTableCategoryDef } from "gen/model/fundFamilyUseAllocationTableCategoryDef";
import type { FundFamilyUseAllocationTableColumnDef } from "gen/model/fundFamilyUseAllocationTableColumnDef";
import { FundFamilyUseAllocationTableDef } from "gen/model/fundFamilyUseAllocationTableDef";
import { FundFamilyUseAllocationTableDefHeaders } from "gen/model/fundFamilyUseAllocationTableDefHeaders";
import { FundFamilyUseAllocationTableFundFamilyDef } from "gen/model/fundFamilyUseAllocationTableFundFamilyDef";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { FundFamilyUseAllocationTableRowColumnData } from "gen/model/fundFamilyUseAllocationTableRowColumnData";
import { FundFamilyUseAllocationTableRowFunds } from "gen/model/fundFamilyUseAllocationTableRowFunds";
import { ManagementFeeDistributionMethodologyEnum } from "gen/model/managementFeeDistributionMethodologyEnum";
import { useCallback } from "react";
import {
  FundFamilyUseAllocationTableContext,
  FundFamilyUseAllocationTableGetErrorForCellFn,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import {
  FundFamilyUseAllocationTableCategoryRow,
  FundFamilyUseAllocationTableFundFamilyOrFundDef,
  FundFamilyUseAllocationTableGenericAllocationRowAmounts,
  FundFamilyUseAllocationTableRowOrCategory,
  FundFamilyUseAllocationTableTotalRowData,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableTypes";
import {
  flattenApiErrorData,
  getApiErrorFromResponse,
} from "shared/utils/ErrorUtils";
import { isTruthy } from "shared/utils/utils";
import { match } from "ts-pattern";

export const createFundFamilyUseAllocationTableEmptyRow = (
  tableDef: FundFamilyUseAllocationTableDef,
  category: FundFamilyUseAllocationTableCategoryDef = tableDef.categories[0],
): FundFamilyUseAllocationTableRow => ({
  id: crypto.randomUUID(),
  category_id: category.id,
  is_auto_allocate: category.allow_auto_allocate,
  fund_family: createFundFamilyUseAllocationTableEmptyRowAmounts(tableDef),
  funds: createFundFamilyUseAllocationTableEmptyRowFunds(tableDef),
  column_data: createFundFamilyUseAllocationTableEmptyColumnData(tableDef),
});

// TODO: Not sure if this belongs here or in feature-specific code (onRowAdd)
const createFundFamilyUseAllocationTableEmptyRowAmountsMeta = (
  amountType: FundFamilyUseAllocationTableAmountTypeDef,
) =>
  match(amountType.meta)
    .with(
      { type: ManagementFeeDistributionMethodologyEnum.fee_break_distribution },
      (meta): FundFamilyUseAllocationTableAmountMeta => ({
        type: ManagementFeeDistributionMethodologyEnum.fee_break_distribution,
        fee_id: meta.fee_id,
      }),
    )
    .with(
      { type: ManagementFeeDistributionMethodologyEnum.fee_holdback },
      (meta): FundFamilyUseAllocationTableAmountMeta => ({
        type: ManagementFeeDistributionMethodologyEnum.fee_holdback,
        fee_id: meta.fee_id,
      }),
    )
    .otherwise(() => undefined);

const createFundFamilyUseAllocationTableEmptyRowAmounts = (
  tableDef: FundFamilyUseAllocationTableDef,
): FundFamilyUseAllocationTableGenericAllocationRowAmounts =>
  tableDef.amount_types.reduce<FundFamilyUseAllocationTableGenericAllocationRowAmounts>(
    (acc, amountType) => ({
      ...acc,
      [amountType.id]: {
        amount: "",
        meta: createFundFamilyUseAllocationTableEmptyRowAmountsMeta(amountType),
      },
    }),
    {},
  );

const createFundFamilyUseAllocationTableEmptyRowFunds = (
  tableDef: FundFamilyUseAllocationTableDef,
): FundFamilyUseAllocationTableRowFunds =>
  tableDef.funds.reduce<FundFamilyUseAllocationTableRowFunds>(
    (acc, fund) => ({
      ...acc,
      [fund.fund_id]:
        createFundFamilyUseAllocationTableEmptyRowAmounts(tableDef),
    }),
    {},
  );

const createFundFamilyUseAllocationTableEmptyColumnData = (
  tableDef: FundFamilyUseAllocationTableDef,
): FundFamilyUseAllocationTableRowColumnData =>
  [tableDef.headers, tableDef.trailers]
    .filter(isTruthy)
    .flat()
    .reduce(
      (acc, columnDef) => ({
        ...acc,
        [columnDef.id]: undefined,
      }),
      {
        type: tableDef.column_data_type,
      },
    ) as FundFamilyUseAllocationTableRowColumnData;

export const isFundFamilyUseAllocationTableTotalRowNode = (
  node: IRowNode<FundFamilyUseAllocationTableRowOrCategory> | null | undefined,
) =>
  Boolean(
    (node?.data as FundFamilyUseAllocationTableTotalRowData | undefined)
      ?.isTotalRow,
  );

export const isFundFamilyUseAllocationTableTotalHeader = (
  params: CellClassParams | ColSpanParams | ICellRendererParams,
) => {
  const displayedColumns = params.api.getAllDisplayedColumns() as
    | Column[]
    | undefined;
  const isFirstColumn = displayedColumns?.[0] === params.column;
  return (
    isFirstColumn && isFundFamilyUseAllocationTableTotalRowNode(params.node)
  );
};

export const fundFamilyUseAllocationTableLeafHeaders = (
  headers: FundFamilyUseAllocationTableDefHeaders | undefined,
): FundFamilyUseAllocationTableColumnDef[] =>
  headers?.reduce(
    (acc, header) =>
      header.children
        ? [...acc, ...fundFamilyUseAllocationTableLeafHeaders(header.children)]
        : [...acc, header],
    [] as FundFamilyUseAllocationTableColumnDef[],
  ) ?? [];

export const fundFamilyUseAllocationTableTotalHeaderColSpan = (
  params: ColSpanParams,
): number => {
  const { tableDef }: FundFamilyUseAllocationTableContext = params.context;
  const { headers, enable_row_add_remove } = tableDef;
  const leafCols = fundFamilyUseAllocationTableLeafHeaders(headers);
  const moneyColIndex = leafCols.findIndex((header) => header.type === "money");
  const colSpan = moneyColIndex > 0 ? moneyColIndex - 1 : leafCols.length;
  return (
    colSpan +
    (enable_row_add_remove ? 1 : 0) +
    // + 1 for Auto Allocate column
    1
  );
};

export const isFundFamilyDef = (
  fundFamilyOrFundDef: FundFamilyUseAllocationTableFundFamilyOrFundDef,
): fundFamilyOrFundDef is FundFamilyUseAllocationTableFundFamilyDef =>
  (
    fundFamilyOrFundDef as {
      fund_family_id: string | undefined;
    }
  ).fund_family_id !== undefined;

export const getPrefixForAllocationFundFamilyOrFundDef = (
  fundFamilyOrFundDef: FundFamilyUseAllocationTableFundFamilyOrFundDef,
) =>
  isFundFamilyDef(fundFamilyOrFundDef)
    ? "fund_family"
    : `funds.${fundFamilyOrFundDef.fund_id}`;

export const isFundFamilyUseAllocationTableRowEditable = (
  node: IRowNode<FundFamilyUseAllocationTableRow>,
) =>
  Boolean(!node.selectable || (node.isSelected() && !node.data?.is_read_only));

export const useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse =
  (
    responseError: unknown,
    rowIdToIndex: (rowId: string) => number | undefined,
    pathPrefix?: string,
  ) =>
    useCallback<FundFamilyUseAllocationTableGetErrorForCellFn>(
      (params) => {
        const colFieldOrId =
          params.column?.getColDef().field || params.column?.getColId();
        const rowId = params.node.id;
        if (!colFieldOrId || !rowId) {
          return undefined;
        }
        const originalIndex = rowIdToIndex(rowId) ?? -1;
        if (originalIndex === -1) {
          return undefined;
        }
        const apiError = getApiErrorFromResponse(responseError);
        const apiErrorData = apiError?.data;
        if (!apiErrorData) {
          return undefined;
        }
        const path = [
          pathPrefix ?? "rows",
          originalIndex,
          colFieldOrId.split("."),
        ]
          .flat()
          .join(".");
        return flattenApiErrorData(apiErrorData)
          .filter(
            ({ fieldPath }) =>
              fieldPath === path || fieldPath.startsWith(`${path}.`),
          )
          .map(({ error }) => error)
          .join(", ");
      },
      [pathPrefix, responseError, rowIdToIndex],
    );

export const isFundFamilyAllocationTableRow = (
  rowOrCategory: FundFamilyUseAllocationTableRowOrCategory | undefined,
): rowOrCategory is FundFamilyUseAllocationTableRow =>
  Boolean(rowOrCategory && !("is_category" in rowOrCategory));

export const isFundFamilyAllocationTableCategoryRow = (
  rowOrCategory: FundFamilyUseAllocationTableRowOrCategory | undefined,
): rowOrCategory is FundFamilyUseAllocationTableCategoryRow =>
  Boolean(
    rowOrCategory &&
      "is_category" in rowOrCategory &&
      rowOrCategory.is_category,
  );

export const fundFamilyUseAllocationTableRefreshTotals = (
  api: GridApi<FundFamilyUseAllocationTableRow>,
) => {
  if (api.isDestroyed()) {
    return;
  }
  api.refreshClientSideRowModel("aggregate");
  const totalNode = api.getPinnedBottomRow(0);
  if (totalNode && isFundFamilyUseAllocationTableTotalRowNode(totalNode)) {
    api.refreshCells({ rowNodes: [totalNode] });
  }
};
